// Trip code is required & globally unique on the backend, so we seed it with
// `T-YYMMDD-HHmm` (operator can overwrite).

import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';

import { useCreateTrip } from '../../features/warehouse/useTrips.js';
import { extractErrorMessage } from '../../features/warehouse/warehouseUtils.js';

function defaultTripCode() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `T-${String(d.getFullYear()).slice(2)}${pad(d.getMonth() + 1)}${pad(
    d.getDate()
  )}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function emptyForm() {
  return {
    code: defaultTripCode(),
    plate: '',
    driver: '',
    customer: '',
    plannedDeparture: '',
    notes: '',
  };
}

export default function CreateTripModal({ show, onHide, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);
  const createTrip = useCreateTrip();

  // Refresh the code + clear stale errors each time the modal re-opens.
  useEffect(() => {
    if (show) {
      setForm(emptyForm());
      setError(null);
    }
  }, [show]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    const body = { ...form };
    if (!body.plannedDeparture) delete body.plannedDeparture;
    createTrip.mutate(body, {
      onSuccess: (trip) => {
        onCreated?.(trip);
        onHide();
      },
      onError: (err) => {
        setError(extractErrorMessage(err, 'Failed to create trip'));
      },
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>New shipment trip</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Trip code *</Form.Label>
            <Form.Control
              type="text"
              required
              value={form.code}
              onChange={e => update('code', e.target.value)}
              placeholder="T-240410-1530"
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Plate</Form.Label>
            <Form.Control
              type="text"
              value={form.plate}
              onChange={e => update('plate', e.target.value)}
              placeholder="B 123 XYZ"
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Driver</Form.Label>
            <Form.Control
              type="text"
              value={form.driver}
              onChange={e => update('driver', e.target.value)}
              placeholder="Ion Popescu"
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Customer</Form.Label>
            <Form.Control
              type="text"
              value={form.customer}
              onChange={e => update('customer', e.target.value)}
              placeholder="Electrica SA"
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Planned departure</Form.Label>
            <Form.Control
              type="datetime-local"
              value={form.plannedDeparture}
              onChange={e => update('plannedDeparture', e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.notes}
              onChange={e => update('notes', e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={createTrip.isPending}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={createTrip.isPending}>
            {createTrip.isPending ? 'Creating…' : 'Create trip'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
